import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { formatCurrency } from "../../utils/billing-utils";
import type { ExternalRep, ExternalRepPlanType } from "../types/commissions.types";
import { CommissionTableEmptyState } from "./commission-table-empty-state";

interface ExternalRepsTableProps {
  reps: ExternalRep[];
  quarterLabel: string;
  onRowClick: (rep: ExternalRep) => void;
  companyIdToName?: Record<string, string>;
  repIdToEstimatedCommission?: Record<string, number>;
}

const PLAN_TYPE_LABELS: Record<ExternalRepPlanType, string> = {
  flat_percent: "Flat percent",
  initial_and_subsequent: "Initial and subsequent",
  category_bucketed: "Category bucketed",
  net_of_overhead_manual: "Net of overhead (manual)",
};

function getCustomersCellContent(
  rep: ExternalRep,
  companyIdToName?: Record<string, string>
): string {
  const ids = rep.assignedCustomerIds ?? [];
  const lines = rep.referralLines ?? [];
  const lineIds = lines
    .map((l) => l.companyId)
    .filter((id): id is string => id != null && id !== "");
  const uniqueIds = [...new Set([...ids, ...lineIds])];
  if (uniqueIds.length === 0) return "—";
  if (uniqueIds.length === 1) {
    const id = uniqueIds[0];
    const lineWithName = lines.find((l) => l.companyId === id);
    const name = lineWithName?.customerName ?? companyIdToName?.[id];
    return name ?? "—";
  }
  return `${uniqueIds.length} customers`;
}

function getAverageRate(rep: ExternalRep): string {
  const lines = rep.referralLines ?? [];
  if (lines.length > 0) {
    const sum = lines.reduce((acc, l) => acc + (l.rate ?? 0), 0);
    return `${(sum / lines.length).toFixed(1)}%`;
  }
  if (rep.initialRatePercent != null) {
    return `${rep.initialRatePercent}%`;
  }
  return "—";
}

const ExternalRepsTable: React.FC<ExternalRepsTableProps> = ({
  reps,
  quarterLabel,
  onRowClick,
  companyIdToName,
  repIdToEstimatedCommission,
}) => {
  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={portalDs.tableHeadCell}>Name</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Email</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Plan</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Customers</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Rate*</TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Est. Commission ({quarterLabel})
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.length === 0 ? (
            <CommissionTableEmptyState
              colSpan={6}
              title="No external reps yet"
              description="Add an external rep to assign customers and commission plans."
            />
          ) : (
            reps.map((rep) => (
              <TableRow
                key={rep.id}
                className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive)}
                onClick={() => onRowClick(rep)}
              >
                <TableCell className={cn(portalDs.tableCell, "font-medium")}>
                  {rep.name ?? rep.organizationName ?? "—"}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {rep.email ?? rep.contactEmail ?? "—"}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {PLAN_TYPE_LABELS[rep.planType] ?? rep.planType}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {getCustomersCellContent(rep, companyIdToName)}
                </TableCell>
                <TableCell className={portalDs.tableCell}>{getAverageRate(rep)}</TableCell>
                <TableCell className={portalDs.tableCell}>
                  {repIdToEstimatedCommission?.[rep.id] != null
                    ? formatCurrency(repIdToEstimatedCommission[rep.id])
                    : rep.estimatedPayoutForQuarter != null
                      ? formatCurrency(rep.estimatedPayoutForQuarter)
                      : "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <p className={cn(portalDs.caption, "border-t border-[#EDEEF2] bg-[#F7F8FA] px-4 py-2.5")}>
        * Rate is the average of all line rates for this rep, or the base rate when no lines exist.
      </p>
    </div>
  );
};

export default ExternalRepsTable;
